import {
  CHANGE_CALENDAR_TIMEZONE,
  type ChangeCalendarItem,
  changeWindowOverlapsBerlinDay,
  fromZonedTime,
  zonedDateTimeParts,
} from '@gb-ticket/shared-types';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export type BerlinDateParts = { year: number; month: number; day: number };

/** Upcoming changes whose window overlaps [now, now + daysAhead]. Sorted by start. */
export function selectUpcomingChanges(
  items: readonly ChangeCalendarItem[],
  options: { now?: Date; daysAhead?: number; limit?: number } = {},
): ChangeCalendarItem[] {
  const now = options.now ?? new Date();
  const daysAhead = options.daysAhead ?? 7;
  const limit = options.limit ?? 5;
  const horizonEnd = new Date(now.getTime() + daysAhead * MS_PER_DAY);
  return selectChangesOverlappingRange(items, now, horizonEnd).slice(0, limit);
}

export function selectChangesOverlappingRange(
  items: readonly ChangeCalendarItem[],
  rangeStart: Date,
  rangeEnd: Date,
): ChangeCalendarItem[] {
  const startMs = rangeStart.getTime();
  const endMs = rangeEnd.getTime();
  return [...items]
    .filter((item) => {
      const start = new Date(item.scheduledStart).getTime();
      const end = new Date(item.scheduledEnd).getTime();
      return start < endMs && end > startMs;
    })
    .sort((a, b) => new Date(a.scheduledStart).getTime() - new Date(b.scheduledStart).getTime());
}

export function berlinDateParts(date: Date = new Date()): BerlinDateParts {
  const parts = zonedDateTimeParts(date, CHANGE_CALENDAR_TIMEZONE);
  return { year: parts.year, month: parts.month, day: parts.day };
}

export function berlinYearMonth(date: Date = new Date()): { year: number; month: number } {
  const { year, month } = berlinDateParts(date);
  return { year, month };
}

export function nextBerlinYearMonth(year: number, month: number): { year: number; month: number } {
  if (month === 12) {
    return { year: year + 1, month: 1 };
  }
  return { year, month: month + 1 };
}

export function changeOverlapsBerlinDay(
  item: ChangeCalendarItem,
  year: number,
  month: number,
  day: number,
): boolean {
  return changeWindowOverlapsBerlinDay(item.scheduledStart, item.scheduledEnd, year, month, day);
}

/** Monday 00:00 → next Monday 00:00 in Europe/Berlin for the week containing `date`. */
export function berlinWeekRange(date: Date = new Date()): { start: Date; end: Date } {
  const parts = berlinDateParts(date);
  const noon = fromZonedTime(
    CHANGE_CALENDAR_TIMEZONE,
    parts.year,
    parts.month,
    parts.day,
    12,
    0,
    0,
  );
  const weekdayName = new Intl.DateTimeFormat('en-US', {
    timeZone: CHANGE_CALENDAR_TIMEZONE,
    weekday: 'short',
  }).format(noon);
  const mondayOffset: Record<string, number> = {
    Mon: 0,
    Tue: 1,
    Wed: 2,
    Thu: 3,
    Fri: 4,
    Sat: 5,
    Sun: 6,
  };
  const daysFromMonday = mondayOffset[weekdayName] ?? 0;
  const mondayUtc = new Date(Date.UTC(parts.year, parts.month - 1, parts.day - daysFromMonday));
  const start = fromZonedTime(
    CHANGE_CALENDAR_TIMEZONE,
    mondayUtc.getUTCFullYear(),
    mondayUtc.getUTCMonth() + 1,
    mondayUtc.getUTCDate(),
    0,
    0,
    0,
  );
  const nextMondayUtc = new Date(
    Date.UTC(mondayUtc.getUTCFullYear(), mondayUtc.getUTCMonth(), mondayUtc.getUTCDate() + 7),
  );
  const end = fromZonedTime(
    CHANGE_CALENDAR_TIMEZONE,
    nextMondayUtc.getUTCFullYear(),
    nextMondayUtc.getUTCMonth() + 1,
    nextMondayUtc.getUTCDate(),
    0,
    0,
    0,
  );
  return { start, end };
}

export function selectThisWeekChanges(
  items: readonly ChangeCalendarItem[],
  now: Date = new Date(),
): ChangeCalendarItem[] {
  const { start, end } = berlinWeekRange(now);
  return selectChangesOverlappingRange(items, start, end);
}

/** Calendar hub URL for a Change window start (Europe/Berlin month). */
export function changeCalendarPathForInstant(iso: string): string {
  const { year, month } = berlinYearMonth(new Date(iso));
  return `/helpdesk/changes?jahr=${year}&monat=${month}`;
}
